import React from 'react';
import Alert from '@material-ui/lab/Alert';
import { makeStyles } from '@material-ui/core/styles';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';

// Compact, low-contrast Alert for MUI v4
// Uses makeStyles to override the default (saturated) palette colors
// with lighter tints, and tightens padding/font-size for a denser look.

const useStyles = makeStyles((theme) => ({
  root: {
    padding: '2px 12px',
    fontSize: '0.8125rem',
    alignItems: 'center',
    borderRadius: 4,
    marginBottom: '12px'
  },
  icon: {
    fontSize: '1.1rem',
    padding: '7px 0',
    marginRight: 8,
  },
  message: {
    padding: '6px 0',
  },
  action: {
    padding: '0 0 0 8px',
    marginRight: -4,
  },
  closeIcon: {
    fontSize: '1rem',
    padding: 4,
  },
  action: {
    padding: '0 0 0 8px',
    marginRight: -4,
    alignItems: 'center',
    '& .MuiIconButton-root': {
      padding: 4,
    },
    '& svg': {
      fontSize: '1.05rem',
    },
  },
  // MUI v4 default success/info/warning/error use saturated fill colors
  // (light/dark[value] pairs). Override with muted tints + dark text.
  standardSuccess: {
    backgroundColor: '#EDF7ED',
    color: '#2E7D32',
    '& $icon': { color: '#4CAF50' },
  },
  standardInfo: {
    backgroundColor: '#E8F4FD',
    color: '#0B5FA5',
    '& $icon': { color: '#4FA3E3' },
  },
  standardWarning: {
    backgroundColor: '#FFF8E6',
    color: '#8A6100',
    '& $icon': { color: '#F0B429' },
  },
  standardError: {
    backgroundColor: '#FDEDED',
    color: '#B23A3A',
    '& $icon': { color: '#E57373' },
  },
}));

const MUI_SEVERITIES = ['success', 'info', 'warning', 'error'];

const LOG_LEVEL_MAP = {
  warn: 'warning',
  fatal: 'error',
  critical: 'error',
  crit: 'error',
  err: 'error',
  debug: 'info',
  trace: 'info',
  notice: 'info',
  ok: 'success',
};

/**
 * CompactAlert
 * Drop-in replacement for MUI v4's <Alert />, but smaller and softer.
 *
 * Props: same as @material-ui/lab Alert (severity, onClose, action, etc.)
 */
export default function CompactAlert({ severity = 'info', children, onClose, action, ...rest }) {
  const classes = useStyles();

  function toAlertSeverity(level, fallback = 'info') {
    if (!level || typeof level !== 'string') return fallback;
    const normalized = level.trim().toLowerCase();
    if (MUI_SEVERITIES.includes(normalized)) return normalized;
    return LOG_LEVEL_MAP[normalized] ?? fallback;
  }

  const safeSeverity = toAlertSeverity(severity);

  return (
    <Alert
      severity={safeSeverity}
      onClose={onClose}
      action={
        action !== undefined ? action : onClose ? (
          <IconButton size="small" aria-label="close" color="inherit" onClick={onClose}>
            <CloseIcon className={classes.closeIcon} />
          </IconButton>
        ) : undefined
      }
      classes={{
        root: classes.root,
        icon: classes.icon,
        message: classes.message,
        action: classes.action,
        standardSuccess: classes.standardSuccess,
        standardInfo: classes.standardInfo,
        standardWarning: classes.standardWarning,
        standardError: classes.standardError,
      }}
      {...rest}
    >
      {children}
    </Alert>
  );
}
